import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pin, SmilePlus, TriangleAlert, X } from "lucide-react";
import { api } from "@/lib/api";
import { CARD, Face } from "@/city3d/Hud";
import { card } from "@/hud/Notices";

const WHERE = { docks: "Docks", uptown: "Uptown" };
const SEEN = "xf_chat_seen";
const REACTIONS = [["fire", "🔥"], ["lol", "😂"], ["love", "❤️"], ["eyes", "👀"], ["gem", "💎"]]; // taste-skill-ignore: reactions are what people send, not UI
const EMOJI = Object.fromEntries(REACTIONS);
const byTime = (a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0);
const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  return m < 1 ? "now" : m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d`;
};

// The city chat: polls every few seconds while open, more slowly while closed so the button can count what's new and
// mentions still arrive. Each poll brings what was posted, reacted to or removed since the last one.
function useChat(open, onMentions) {
  const [messages, setMessages] = useState([]);
  const [muted, setMuted] = useState(false);
  const [pin, setPin] = useState(null);
  const cursor = useRef(null);
  const mentions = useRef(onMentions);
  mentions.current = onMentions;
  const merge = useCallback((list, removed = []) => setMessages((ms) => {
    const gone = new Set(removed), next = new Map(ms.filter((m) => !gone.has(m.id)).map((m) => [m.id, m]));
    for (const m of list) if (!gone.has(m.id)) next.set(m.id, m);
    return [...next.values()].sort(byTime).slice(-200);
  }), []);
  const pull = useCallback(() => api(`/chat${cursor.current ? `?after=${encodeURIComponent(cursor.current)}` : ""}`).then((d) => {
    cursor.current = d.cursor;
    setMuted(Boolean(d.me?.muted));
    setPin(d.pin ?? null);
    merge(d.messages, d.removed);
    if (d.me?.mentions?.length) mentions.current(d.me.mentions);
  }).catch(() => {}), [merge]);
  useEffect(() => {
    pull();
    const id = setInterval(pull, open ? 3000 : 10000);
    return () => clearInterval(id);
  }, [pull, open]);
  const send = async (text) => merge([await api("/chat", { method: "POST", body: { text } })]);
  const react = (id, r) => api(`/chat/${id}/react`, { method: "POST", body: { r } }).then((m) => merge([m])).catch((e) => card({ icon: TriangleAlert, tone: "red", title: e.message }));
  return { messages, muted, pin, send, react };
}

// @handles in a message open that person's house; your own handle is highlighted.
const Text = ({ text, meK, onVisit }) => text.split(/(@\w{1,15})/g).map((part, i) => {
  if (!/^@\w+$/.test(part)) return <span key={i}>{part}</span>;
  const k = part.slice(1).toLowerCase();
  return (
    <button key={i} type="button" onClick={() => onVisit({ k })}
      className={`font-semibold text-x-accent-ink hover:underline ${k === meK ? "rounded bg-x-accent-soft px-0.5 text-x-accent-ink" : ""}`}>{part}</button>
  );
});

function Reactions({ m, picking, canReact, onReact }) {
  if (!m.reactions.length && !picking) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`chat-reactions-${m.id}`}>
      {m.reactions.map((x) => (
        <button key={x.r} type="button" disabled={!canReact} onClick={() => onReact(x.r)} data-testid={`chat-reaction-${m.id}-${x.r}`}
          className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs tabular-nums transition enabled:hover:scale-110 disabled:cursor-default ${x.mine ? "bg-x-accent-soft ring-1 ring-x-accent" : "bg-x-raised enabled:hover:bg-x-active"}`}>
          <span>{EMOJI[x.r]}</span><span className="font-semibold text-x-ink">{x.n}</span>
        </button>
      ))}
      {picking && (
        <div className="pop flex gap-0.5 rounded-full hud-panel hud-label px-1 py-0.5" data-testid={`chat-react-picker-${m.id}`}>
          {REACTIONS.map(([r, e]) => (
            <button key={r} type="button" onClick={() => onReact(r)} data-testid={`chat-react-${m.id}-${r}`} aria-label={r}
              className="rounded-full px-1 text-base transition-transform hover:scale-125">{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function Message({ m, meK, flash, canReact, onReact, onVisit }) {
  const [picking, setPicking] = useState(false);
  const react = (r) => { setPicking(false); onReact(m.id, r); };
  return (
    <li data-msg={m.id} data-testid={`chat-message-${m.id}`}
      className={`group flex gap-2 rounded-lg px-1 py-0.5 transition-colors duration-700 ${flash ? "bg-x-accent-soft" : ""}`}>
      <button type="button" onClick={() => onVisit(m)} className="shrink-0 self-start transition-transform hover:scale-110">
        <Face p={m} size={28} ring={m.k === meK ? "var(--x-accent)" : "var(--x-line)"} />
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[11px]">
          <button type="button" onClick={() => onVisit(m)} className="truncate text-xs font-semibold text-x-ink hover:underline">@{m.h}</button>
          {m.mayor && <span className="shrink-0 rounded-full bg-x-gold-soft px-1.5 text-[11px] font-semibold text-x-gold-ink" data-testid="chat-mayor-badge">Mayor</span>}
          <span className="shrink-0 text-x-mute">{m.where === "mansion" ? `Mansion #${m.rank}` : WHERE[m.where]}</span>
          <span className="shrink-0 tabular-nums text-x-mute">· {ago(m.at)}</span>
          {canReact && (
            <button type="button" onClick={() => setPicking((p) => !p)} aria-label="React" data-testid={`chat-react-open-${m.id}`}
              className="ml-auto shrink-0 rounded-full p-0.5 text-x-mute transition-opacity hover:bg-x-raised hover:text-x-ink sm:opacity-0 sm:group-hover:opacity-100">
              <SmilePlus size={14} />
            </button>
          )}
        </div>
        <div className="break-words text-sm text-x-ink" data-testid="chat-message-text"><Text text={m.text} meK={meK} onVisit={onVisit} /></div>
        <Reactions m={m} picking={picking} canReact={canReact} onReact={react} />
      </div>
    </li>
  );
}

const NOTE = {
  dark: ["chat-not-lit", "Your lights come on first, then you can chat."],
  muted: ["chat-muted", "You're muted in the city chat. You can still read it."],
};

function Composer({ state, onSend, onSignIn }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (state === "out") {
    return (
      <button type="button" onClick={onSignIn} data-testid="chat-sign-in"
        className="chip-btn w-full py-2 text-sm">Sign in with X to chat</button>
    );
  }
  if (NOTE[state]) return <div className="rounded-lg bg-x-raised px-3 py-2 text-xs text-x-mute" data-testid={NOTE[state][0]}>{NOTE[state][1]}</div>;
  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await onSend(t);
      setText("");
    } catch (x) {
      setErr(x.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="chat-form">
      <div className="flex gap-1.5">
        <input value={text} maxLength={280} onChange={(e) => setText(e.target.value)} placeholder="Say something to the city…" data-testid="chat-input"
          className="min-w-0 flex-1 rounded-full bg-x-raised px-3.5 py-2 text-sm outline-none transition-shadow placeholder:text-x-mute focus:ring-2 focus:ring-x-accent" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send"
          className="chip-btn rounded-full px-4 text-sm">Send</button>
      </div>
      {err && <div className="mt-1 text-xs text-x-neg" data-testid="chat-error">{err}</div>}
      {text.length > 240 && <div className="mt-1 text-right text-[11px] tabular-nums text-x-mute">{280 - text.length} left</div>}
    </form>
  );
}

function ChatWindow({ messages, pin, me, state, target, onSend, onReact, onSignIn, onVisit, onClose, onPin }) {
  const list = useRef();
  useEffect(() => {
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [messages.length]);
  useEffect(() => {
    list.current?.querySelector(`[data-msg="${target?.id}"]`)?.scrollIntoView({ block: "center" });
  }, [target, messages.length]);
  return (
    <div className={`${CARD} hud-thick pop pointer-events-auto fixed inset-x-2 bottom-12 z-30 flex h-[65vh] flex-col sm:absolute sm:inset-x-auto sm:bottom-12 sm:right-3 sm:top-[72px] sm:h-auto sm:w-[380px]`}
      data-testid="chat-panel">
      <div className="flex items-center justify-between border-b border-x-line px-3 py-2.5">
        <div className="leading-tight">
          <h2 className="text-lg font-semibold">City chat</h2>
          <div className="text-[11px] text-x-mute">Everyone in XField, live</div>
        </div>
        <button type="button" onClick={onClose} data-testid="chat-close" aria-label="Close" className="hud-well grid h-7 w-7 place-items-center rounded-full text-x-mute transition-colors hover:bg-x-active hover:text-x-ink"><X className="h-4 w-4" /></button>
      </div>
      {pin && (
        <button type="button" onClick={() => onPin(pin.id)} data-testid="chat-pin"
          className="flex items-start gap-2 border-b border-x-line bg-x-gold-soft px-3 py-1.5 text-left text-xs transition-[filter] hover:brightness-95">
          <Pin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-x-gold" />
          <span className="min-w-0 flex-1"><b>@{pin.h}</b> <span className="rounded-full bg-x-surface px-1.5 text-[11px] font-semibold text-x-gold-ink">Mayor</span>
            <span className="line-clamp-2 break-words text-x-ink"> {pin.text}</span></span>
        </button>
      )}
      <ul ref={list} className="flex-1 space-y-1.5 overflow-y-auto px-2 py-2.5" data-testid="chat-messages">
        {messages.map((m) => (
          <Message key={m.id} m={m} meK={me?.k} flash={target?.id === m.id} canReact={state === "in"} onReact={onReact} onVisit={onVisit} />
        ))}
        {!messages.length && <li className="py-8 text-center text-sm text-x-mute" data-testid="chat-empty">No messages yet. Say hi to the city.</li>}
      </ul>
      <div className="border-t border-x-line p-2.5"><Composer state={state} onSend={onSend} onSignIn={onSignIn} /></div>
    </div>
  );
}

function MentionToast({ m, count, onJump, onClose }) {
  const key = count > 1 ? "many" : m.id;
  return (
    <div className={`${CARD} hud-float flex w-[min(92vw,360px)] items-start gap-2.5 p-3`} data-testid={`mention-toast-${key}`}>
      <Face p={m} size={34} ring="var(--x-accent)" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">{count > 1 ? `You were mentioned ${count} times while you were away` : `@${m.h} mentioned you`}</div>
        {count === 1 && <div className="line-clamp-2 break-words text-xs text-x-mute">{m.text}</div>}
        <div className="mt-2 flex items-center gap-3">
          <button type="button" onClick={onJump} data-testid={`mention-toast-jump-${key}`}
            className="chip-btn px-3 py-1.5 text-xs">Jump in</button>
          <button type="button" onClick={onClose} data-testid={`mention-toast-dismiss-${key}`} className="text-xs text-x-mute hover:text-x-ink">Dismiss</button>
        </div>
      </div>
    </div>
  );
}

// Mentions you haven't seen: shown once as toasts (or one summary when many piled up), then marked seen. A tab in the
// background waits until you come back. With the chat already open the message just lights up instead.
function useMentionAlerts(openRef, jump) {
  const shown = useRef(new Set());
  return useCallback((list) => {
    if (document.hidden) return;
    const fresh = list.filter((m) => !shown.current.has(m.id));
    if (!fresh.length) return;
    fresh.forEach((m) => shown.current.add(m.id));
    api("/chat/mentions/seen", { method: "POST", body: { ids: fresh.map((m) => m.id) } }).catch(() => {});
    if (openRef.current) return jump(fresh[fresh.length - 1].msg_id);
    const show = (m, count, to) => toast.custom((t) => (
      <MentionToast m={m} count={count} onJump={() => { toast.dismiss(t); jump(to); }} onClose={() => toast.dismiss(t)} />
    ), { duration: 12000 });
    if (fresh.length > 3) show(fresh[fresh.length - 1], fresh.length, fresh[0].msg_id);
    else fresh.forEach((m) => show(m, 1, m.msg_id));
  }, [openRef, jump]);
}

// One chat room for the whole city. Anyone can read it; residents whose lights are on can post and react.
// The HUD owns the launcher: `open` and `onOpenChange` control the window, `onUnread` reports what's new.
export function Chat({ user, me, open, onOpenChange, onUnread, onSignIn, onVisit }) {
  const [target, setTarget] = useState(null);
  const openRef = useRef(open);
  openRef.current = open;
  const jump = useCallback((id) => {
    onOpenChange(true);
    setTarget({ id, t: Date.now() });
  }, [onOpenChange]);
  const { messages, muted, pin, send, react } = useChat(open, useMentionAlerts(openRef, jump));
  useEffect(() => {
    if (!target) return;
    const id = setTimeout(() => setTarget(null), 3500);
    return () => clearTimeout(id);
  }, [target]);
  const [seen, setSeen] = useState(() => localStorage.getItem(SEEN));
  const newest = messages[messages.length - 1]?.at || "";
  useEffect(() => {
    if (!newest || (seen !== null && (!open || newest <= seen))) return;
    localStorage.setItem(SEEN, newest);
    setSeen(newest);
  }, [open, newest, seen]);
  const unread = seen === null ? 0 : messages.filter((m) => m.at > seen && m.k !== user?.k).length;
  const state = !user ? "out" : muted ? "muted" : me?.status !== "finished" ? "dark" : "in";
  useEffect(() => { onUnread?.(unread); }, [unread]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  return <ChatWindow messages={messages} pin={pin} me={user} state={state} target={target} onSend={send} onReact={react} onSignIn={onSignIn}
    onClose={() => onOpenChange(false)} onPin={jump} onVisit={(p) => { onOpenChange(window.innerWidth >= 640); onVisit(p); }} />;
}
